import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { BotHistoryItem } from 'maia'
import {
   chat,
   ChatHistoryRequest,
   getChatHistory,
   rateChat,
   RequestType,
} from '../api/chat'

export type RatingStatus = 'idle' | 'sending' | 'success' | 'error'

export type ChatWrapper = {
   isPristine: boolean
   isFetching: boolean
   isFetchingNewMessage: boolean
   hasError: boolean
   isRating: boolean
   ratingStatus: RatingStatus
   ratings: Record<string, boolean>
   data?: {
      threadId?: string
      items: Array<BotHistoryItem>
   }
}

const initialState: ChatWrapper = {
   isPristine: true,
   isFetching: false,
   isFetchingNewMessage: false,
   hasError: false,
   isRating: false,
   ratingStatus: 'idle',
   ratings: {},
   data: {
      items: [],
   },
}

export const chatHistoryThunk = createAsyncThunk('chat/history', async (request: ChatHistoryRequest, { rejectWithValue }) => {
   const response = await getChatHistory(request)
   if (response) {
      return response
   }
   return rejectWithValue({
      answer: 'Error fetching chat history',
   })
})

export const chatThunk = createAsyncThunk('chat', async (request: RequestType, { rejectWithValue }) => {
   const response = await chat(request)
   if (response) {
      return response
   }
   return rejectWithValue({
      answer: 'Error fetching chat response',
   })
})

type RateConversationArgs = {
   threadId: string
   isPositive: boolean
   account?: string
   locale?: string
   messageId?: string
}

export const rateConversationThunk = createAsyncThunk('chat/rate', async ({ threadId, isPositive, account, locale, messageId }: RateConversationArgs, { rejectWithValue }) => {
   const ok = await rateChat({ threadId, isPositive, account, locale, messageId })
   if (ok) return { isPositive }
   return rejectWithValue({ answer: 'Error rating chat conversation' })
})

export const chatSlice = createSlice({
   name: 'chat',
   initialState,
   reducers: {
      addUserChatMessage: (state, action: PayloadAction<{ content: string }>) => {
         state.data?.items.unshift({
            role: 'USER',
            content: action.payload.content,
            timestamp: new Date().toISOString(),
         })
      },
      setMessageRating: (state, action: PayloadAction<{ messageKey: string; isPositive: boolean }>) => {
         state.ratings[action.payload.messageKey] = action.payload.isPositive
      },
      clear: () => {
         return initialState
      },
   },
   extraReducers(builder) {
      builder.addCase(chatHistoryThunk.pending, (state) => {
         state.isFetching = true
         state.hasError = false
      })
      builder.addCase(chatHistoryThunk.fulfilled, (state, action) => {
         const { payload } = action
         state.isPristine = false
         state.isFetching = false
         state.ratings = {}
         if (state.data) {
            state.data.threadId = payload.threadId
            state.data.items = payload.messages.reverse()
         }
         payload.messages.forEach((msg) => {
            if (msg.id && msg.rating) {
               state.ratings[msg.id] = msg.rating.positive
            }
         })
      })
      builder.addCase(chatHistoryThunk.rejected, (state) => {
         state.isPristine = false
         state.isFetching = false
         state.hasError = true
      })
      builder.addCase(chatThunk.pending, (state) => {
         state.isPristine = false
         state.isFetchingNewMessage = true
      })
      builder.addCase(chatThunk.fulfilled, (state, action) => {
         const { payload } = action
         state.isPristine = false
         state.isFetchingNewMessage = false
         state.data?.items.unshift({
            role: 'ASSISTANT',
            content: payload.answer,
            data: payload.data,
            timestamp: new Date().toISOString(),
            id: payload.messageId,
         })
         if (payload.threadId && state.data) {
            state.data.threadId = payload.threadId
         }
      })
      builder.addCase(chatThunk.rejected, (state) => {
         state.isPristine = false
         state.isFetchingNewMessage = false
      })
      builder.addCase(rateConversationThunk.pending, (state) => {
         state.isRating = true
         state.ratingStatus = 'sending'
      })
      builder.addCase(rateConversationThunk.fulfilled, (state) => {
         state.isRating = false
         state.ratingStatus = 'success'
      })
      builder.addCase(rateConversationThunk.rejected, (state, action) => {
         state.isRating = false
         state.ratingStatus = 'error'
         const messageKey = action.meta.arg.messageId
         if (messageKey) delete state.ratings[messageKey]
      })
   },
})

export const { clear, addUserChatMessage, setMessageRating } = chatSlice.actions
export default chatSlice.reducer